import { createContext, useContext, useState, useCallback, useRef } from 'react';
import ToastStack from '../components/common/Toast/ToastStack';

/**
 * ToastContext — global queue-based toast system.
 *
 * SETUP: Wrap your app root with <ToastProvider> once.
 * Then in any component: const { toast } = useToast();
 *
 * Usage:
 *   toast('Added to bag!')                        // success (default)
 *   toast('Something went wrong', 'error')
 *   toast('Size chart updated', 'info')
 *   toast('Low stock — only 2 left', 'warning')
 *
 * That's the entire API. Nothing else needed.
 */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const timerRef = useRef(null);

  const dismiss = useCallback((id) => {
    setQueue((q) => q.map((t) => (t.id === id ? { ...t, visible: false } : t)));
    // Remove from DOM after exit animation completes
    setTimeout(() => {
      setQueue((q) => q.filter((t) => t.id !== id));
    }, 400);
  }, []);

  const toast = useCallback((message, type = 'success', duration = 3000) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    setQueue((q) => {
      // Max 3 visible toasts — drop oldest if over limit
      const trimmed = q.length >= 3 ? q.slice(1) : q;
      return [...trimmed, { id, message, type, duration, visible: true }];
    });

    // Auto-dismiss
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => dismiss(id), duration);

    return id; // caller can use this to dismiss early if needed
  }, [dismiss]);

  // Export both 'toast' and 'showToast' for backward compatibility
  const showToast = toast;

  return (
    <ToastContext.Provider value={{ toast, showToast, dismiss }}>
      {children}
      <ToastStack queue={queue} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
